import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Stack,
  Breadcrumbs,
  Link,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  PlayArrow as TestIcon,
  NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import VisualWorkflowBuilder from '../../components/automation/VisualWorkflowBuilder';
import automationService from '../../services/automationService';

const WorkflowEditor = () => {
  const { id, workflowId } = useParams();
  const navigate = useNavigate();
  const projectId = id; // The project ID is the 'id' param from the parent route
  // Check if we're creating a new workflow - workflowId is undefined for the /new route
  const isNew = !workflowId;
  
  // Try to get project from outlet context if available
  let contextData = {};
  try {
    contextData = useOutletContext() || {};
  } catch (e) {
    // If not in outlet context, that's okay
  }
  
  // Debug log to check projectId
  console.log('WorkflowEditor - projectId:', projectId, 'workflowId:', workflowId, 'isNew:', isNew);
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [workflow, setWorkflow] = useState({
    name: '',
    description: '',
    category: 'task_management',
    nodes: [],
    edges: [],
    trigger: {
      type: 'manual',
      config: {}
    },
    settings: {
      enabled: false,
      priority: 5,
      maxExecutions: 100,
      scheduling: {
        enabled: false
      }
    },
    chainBehavior: {
      allowChaining: true,
      maxChainDepth: 5
    },
    safety: {
      loopPrevention: {
        enabled: true,
        maxIterations: 5
      },
      rateLimiting: {
        enabled: true,
        maxExecutions: 10,
        window: 60
      },
      circuitBreaker: {
        enabled: true,
        failureThreshold: 5,
        resetTimeout: 60
      },
      maxConcurrentExecutions: 3
    }
  });
  
  const [errors, setErrors] = useState([]);
  const [testResult, setTestResult] = useState(null);

  // Load workflow if editing
  useEffect(() => {
    if (!isNew && workflowId) {
      loadWorkflow();
    }
  }, [workflowId, isNew]);

  const loadWorkflow = async () => {
    try {
      setLoading(true);
      const response = await automationService.getWorkflow(workflowId);
      // Handle different response structures
      const workflowData = response.data?.workflow || response.workflow;
      if (workflowData) {
        setWorkflow(workflowData);
      } else {
        throw new Error('Invalid workflow data structure');
      }
    } catch (error) {
      setErrors(['Failed to load workflow: ' + (error.message || 'Unknown error')]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (workflowData) => {
    try {
      setSaving(true);
      setErrors([]);
      
      // Use workflowData from VisualWorkflowBuilder which has transformed edges
      // Only merge non-visual fields from workflow state
      const { nodes: _, edges: __, ...workflowMetadata } = workflow;
      const dataToSave = workflowData 
        ? { ...workflowMetadata, ...workflowData }
        : workflow;
      
      if (isNew) {
        const response = await automationService.createWorkflow(projectId, dataToSave);
        // Handle different response structures
        const createdWorkflow = response.data?.workflow || response.workflow;
        if (!createdWorkflow?._id) {
          throw new Error('Failed to create workflow - invalid response');
        }
        // Update the workflow state with the created workflow
        setWorkflow(createdWorkflow);
        navigate(`/projects/${projectId}/automation/workflows/${createdWorkflow._id}/edit`);
      } else {
        const response = await automationService.updateWorkflow(workflowId, dataToSave);
        // Reload the workflow to get the latest data from backend
        await loadWorkflow();
      }
      
      setTestResult({ type: 'success', message: 'Workflow saved successfully!' });
    } catch (error) {
      setErrors([error.message || 'Failed to save workflow']);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (workflowData) => {
    try {
      setTestResult(null);
      
      // Save first if new
      if (isNew) {
        await handleSave(workflowData);
        return;
      }
      
      // Make sure we use the backend-transformed data for testing
      const { nodes: _, edges: __, ...workflowMetadata } = workflow;
      const dataToTest = workflowData 
        ? { ...workflowMetadata, ...workflowData }
        : workflow;
      
      const response = await automationService.testWorkflow(workflowId, {
        workflow: dataToTest,
        testData: {
          task: {
            _id: 'test-task-id',
            title: 'Test Task',
            status: 'todo'
          }
        }
      });
      
      setTestResult({
        type: 'info',
        message: 'Test execution completed successfully. Check the execution logs for results.',
        execution: response.data || response.execution || response
      });
    } catch (error) {
      setTestResult({
        type: 'error',
        message: error.message || 'Failed to test workflow'
      });
    }
  };

  const handleBack = () => {
    navigate(`/projects/${projectId}/automation`);
  };

  if (loading) {
    return (
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Paper sx={{ borderRadius: 0, borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="xl">
          <Box sx={{ py: 2 }}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
              <Link
                component="button"
                variant="body1"
                onClick={() => navigate(`/projects/${projectId}`)}
                sx={{ cursor: 'pointer', textDecoration: 'none' }}
              >
                Project
              </Link>
              <Link
                component="button"
                variant="body1"
                onClick={() => navigate(`/projects/${projectId}/automation`)}
                sx={{ cursor: 'pointer', textDecoration: 'none' }}
              >
                Automation
              </Link>
              <Typography color="text.primary">
                {isNew ? 'New Workflow' : 'Edit Workflow'}
              </Typography>
            </Breadcrumbs>
            
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  startIcon={<BackIcon />}
                  onClick={handleBack}
                  variant="outlined"
                >
                  Back
                </Button>
                <TextField
                  value={workflow.name}
                  onChange={(e) => setWorkflow({ ...workflow, name: e.target.value })}
                  placeholder="Workflow Name"
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: 300 }}
                  error={!workflow.name && errors.length > 0}
                  helperText={!workflow.name && errors.length > 0 ? 'Name is required' : ''}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={workflow.settings?.enabled || false}
                      onChange={(e) => setWorkflow({
                        ...workflow,
                        settings: {
                          ...workflow.settings,
                          enabled: e.target.checked
                        }
                      })}
                    />
                  }
                  label="Enabled"
                />
              </Stack>
              
              <Typography variant="caption" color="text.secondary">
                {isNew ? 'Creating new workflow' : `Editing: ${workflow._id}`}
              </Typography>
            </Stack>
          </Box>
        </Container>
      </Paper>

      {/* Alerts */}
      {errors.length > 0 && (
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          {errors.map((error, index) => (
            <Alert key={index} severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          ))}
        </Container>
      )}
      
      {testResult && (
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          <Alert 
            severity={testResult.type} 
            onClose={() => setTestResult(null)}
            action={
              testResult.execution && (
                <Button
                  size="small"
                  onClick={() => navigate(`/projects/${projectId}/automation/executions/${testResult.execution._id}`)}
                >
                  View Execution
                </Button>
              )
            }
          >
            {testResult.message}
          </Alert>
        </Container>
      )}

      {/* Visual Workflow Builder */}
      <Box sx={{ flex: 1, overflow: 'hidden', mt: 2 }}>
        <VisualWorkflowBuilder
          key={workflow._id || 'new'}
          workflow={workflow}
          onChange={(updates) => {
            // Only update non-visual fields in the workflow state
            // The visual builder manages nodes and edges internally
            if (updates.nodes || updates.edges) {
              // Don't update workflow state with visual data
              // This prevents React Flow edge types from polluting the state
              return;
            }
            setWorkflow({ ...workflow, ...updates });
          }}
          onSave={(workflowData) => handleSave(workflowData)}
          onTest={(workflowData) => handleTest(workflowData)}
          readonly={false}
        />
      </Box>
    </Box>
  );
};

export default WorkflowEditor;